import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Sparkles, Rocket, Users, GitFork, MessagesSquare, MapPin, BookOpen,
  Check, ArrowRight, Menu, X, ChevronDown, ShieldCheck, Zap, Cloud, Crown, Building2, Database, Server, HardDrive, Lock,
} from "lucide-react";
import {
  NAV, STATS, FEATURES, MODULE_TABS, BRANCH_ROLES, PLANS, FAQ, DEPLOY_POINTS, HOW_STEPS,
  CONTACT_EMAIL, CURRENCY, ANNUAL_DISCOUNT_NOTE,
} from "@/lib/landingContent";

/* ─────────────────────────────────────────────────────────────
   PRIZM landing page. Palette comes straight from the logo:
   navy ink · indigo → violet → blue → mint (the prism)  ·  magenta → orange → amber (the Z)
   ───────────────────────────────────────────────────────────── */
const CSS = `
.lp { --ink:#111B3A; --ink2:#46506E; --mute:#7C86A2; --line:#E6E9F5; --soft:#F6F7FD;
      --indigo:#2B3BD9; --violet:#7C3AED; --blue:#3B82E6; --cyan:#4FB3E8; --mint:#6EE7A8;
      --magenta:#E0409A; --orange:#F26B3A; --amber:#F5B83D;
      font-family:'Manrope',system-ui,sans-serif; color:var(--ink); background:#fff; }
.lp h1,.lp h2,.lp h3,.lp .lp-display { font-family:'Outfit','Manrope',sans-serif; letter-spacing:-0.02em; }
.lp-grad      { background:var(--indigo); }
.lp-grad-warm { background:var(--orange); }
.lp-grad-text { color:var(--indigo); }
.lp-warm-text { color:#D9541E; }
.lp-btn { display:inline-flex; align-items:center; gap:.5rem; font-weight:600; border-radius:999px; padding:.8rem 1.4rem;
          transition:transform .15s ease, box-shadow .15s ease, background .15s ease; cursor:pointer; white-space:nowrap; }
.lp-btn:hover { transform:translateY(-1px); }
.lp-btn-primary { color:#fff; background:var(--indigo); box-shadow:0 1px 2px rgba(17,27,58,.12); }
.lp-btn-primary:hover { background:#2230B8; box-shadow:0 4px 12px -4px rgba(17,27,58,.35); }
.lp-btn-ghost { color:var(--ink); background:#fff; border:1px solid var(--line); }
.lp-btn-ghost:hover { border-color:#c9cff0; background:var(--soft); }
.lp-card { background:#fff; border:1px solid var(--line); border-radius:20px; transition:transform .2s ease, box-shadow .2s ease, border-color .2s ease; }
.lp-card:hover { transform:translateY(-2px); box-shadow:0 12px 28px -16px rgba(17,27,58,.25); border-color:#d6dbf6; }
.lp-reveal { opacity:0; transform:translateY(18px); transition:opacity .6s ease, transform .6s ease; }
.lp-reveal.in { opacity:1; transform:none; }
.lp-float { animation:lpfloat 6s ease-in-out infinite; }
@keyframes lpfloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
.lp-bar { transform-origin:bottom; animation:lpbar 1.2s cubic-bezier(.2,.8,.2,1) both; }
@keyframes lpbar { from{transform:scaleY(0)} to{transform:scaleY(1)} }
@media (prefers-reduced-motion:reduce){ .lp-float,.lp-bar{animation:none} .lp-reveal{opacity:1;transform:none;transition:none} }
`;

export const LP_CSS = CSS;

const ICONS = { dashboard: LayoutDashboard, ai: Sparkles, agile: Rocket, people: Users, git: GitFork, chat: MessagesSquare, branch: MapPin, docs: BookOpen };
const ICON_BG = ["#2B3BD9", "#7C3AED", "#3B82E6", "#0E9F6E", "#D9541E", "#B7791F", "#111B3A", "#4FB3E8"];

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (!("IntersectionObserver" in window)) { el.classList.add("in"); return undefined; }
    const io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); } }), { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}
function Reveal({ children, className = "", delay = 0 }) {
  const ref = useReveal();
  return <div ref={ref} className={`lp-reveal ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
}

const fmtMoney = (n) => `${CURRENCY}${n.toLocaleString("en-US")}`;

/* ───────────── Nav ───────────── */
function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 8);
    f(); window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);
  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all ${scrolled ? "bg-white/85 backdrop-blur-md border-b" : "bg-transparent"}`} style={{ borderColor: "var(--line)" }} data-testid="lp-nav">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center" aria-label="PRIZM home"><img src="/prizm-logo-light.png" alt="PRIZM" className="h-9 w-auto" /></a>
        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => <a key={n.href} href={n.href} className="text-sm font-medium transition-colors hover:text-[color:var(--indigo)]" style={{ color: "var(--ink2)" }}>{n.label}</a>)}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <Link to="/login" className="text-sm font-semibold px-3 py-2" style={{ color: "var(--ink)" }} data-testid="lp-signin">Sign in</Link>
          <Link to="/demo" className="lp-btn lp-btn-primary text-sm !py-2.5" data-testid="lp-demo">Try the live demo <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <button className="lg:hidden p-2" onClick={() => setOpen((v) => !v)} aria-label="Menu" data-testid="lp-menu">{open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t px-5 py-4 space-y-1" style={{ borderColor: "var(--line)" }}>
          {NAV.map((n) => <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="block py-2.5 font-medium" style={{ color: "var(--ink2)" }}>{n.label}</a>)}
          <div className="pt-3 flex gap-3">
            <Link to="/login" className="lp-btn lp-btn-ghost flex-1 justify-center">Sign in</Link>
            <Link to="/demo" className="lp-btn lp-btn-primary flex-1 justify-center">Try the demo</Link>
          </div>
        </div>
      )}
    </header>
  );
}

/* ───────────── Hero ───────────── */
function DashboardMock() {
  const bars = [38, 56, 44, 72, 61, 84, 70, 92, 78, 96];
  const kpis = [["Employees", "128", "#2B3BD9"], ["Productivity", "87%", "#7C3AED"], ["Projects", "24", "#3B82E6"], ["Org health", "92", "#1fbf8a"]];
  return (
    <div className="relative mx-auto w-full max-w-[560px]" aria-hidden="true">
      <div className="relative rounded-3xl border bg-white shadow-[0_24px_60px_-30px_rgba(17,27,58,.35)] overflow-hidden" style={{ borderColor: "var(--line)" }}>
        <div className="flex items-center gap-1.5 px-4 py-3 border-b" style={{ borderColor: "var(--line)", background: "var(--soft)" }}>
          <span className="w-2.5 h-2.5 rounded-full bg-[#F26B3A]" /><span className="w-2.5 h-2.5 rounded-full bg-[#F5B83D]" /><span className="w-2.5 h-2.5 rounded-full bg-[#6EE7A8]" />
          <span className="ml-3 text-[11px] font-semibold" style={{ color: "var(--mute)" }}>Executive Dashboard · All Branches</span>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-4 gap-2.5">
            {kpis.map(([k, v, c]) => (
              <div key={k} className="rounded-xl p-2.5 border" style={{ borderColor: "var(--line)" }}>
                <div className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: "var(--mute)" }}>{k}</div>
                <div className="lp-display text-xl font-semibold mt-0.5" style={{ color: c }}>{v}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-5 gap-3">
            <div className="col-span-3 rounded-xl border p-3" style={{ borderColor: "var(--line)" }}>
              <div className="text-[10px] font-semibold mb-2" style={{ color: "var(--ink2)" }}>Productivity trend</div>
              <div className="flex items-end gap-1.5 h-24">
                {bars.map((h, i) => <div key={i} className="lp-bar flex-1 rounded-t-md lp-grad" style={{ height: `${h}%`, animationDelay: `${i * 70}ms`, opacity: 0.55 + i * 0.045 }} />)}
              </div>
            </div>
            <div className="col-span-2 rounded-xl border p-3 flex flex-col items-center justify-center" style={{ borderColor: "var(--line)" }}>
              <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
                <circle cx="18" cy="18" r="15.5" fill="none" stroke="#EEF0FA" strokeWidth="4" />
                <circle cx="18" cy="18" r="15.5" fill="none" stroke="#2B3BD9" strokeWidth="4" strokeLinecap="round" strokeDasharray="97.4" strokeDashoffset="14" />
              </svg>
              <div className="text-[10px] font-semibold mt-1" style={{ color: "var(--ink2)" }}>Sprint progress</div>
            </div>
          </div>
          <div className="space-y-2">
            {[["Mobile app v2", 78, "#7C3AED"], ["Client portal", 54, "#3B82E6"], ["Payments revamp", 33, "#F26B3A"]].map(([n, p, c]) => (
              <div key={n} className="flex items-center gap-3 text-[11px]">
                <span className="w-28 truncate font-medium" style={{ color: "var(--ink2)" }}>{n}</span>
                <div className="flex-1 h-1.5 rounded-full" style={{ background: "#EEF0FA" }}><div className="h-full rounded-full" style={{ width: `${p}%`, background: c }} /></div>
                <span className="w-8 text-right font-semibold" style={{ color: c }}>{p}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="lp-float absolute -left-4 sm:-left-10 bottom-10 w-52 rounded-2xl bg-white border shadow-xl p-3" style={{ borderColor: "var(--line)" }}>
        <div className="flex items-center gap-2 text-[11px] font-bold lp-warm-text"><Sparkles className="w-3.5 h-3.5 text-[#E0409A]" />AI suggested action</div>
        <div className="text-[11px] mt-1 leading-snug" style={{ color: "var(--ink2)" }}>Two sprints are trending late. Consider re-balancing work across branches.</div>
      </div>
      <div className="lp-float absolute right-4 -top-5 rounded-2xl bg-white border shadow-xl p-3 flex items-center gap-2" style={{ borderColor: "var(--line)", animationDelay: "1.2s" }}>
        <span className="w-8 h-8 rounded-lg lp-grad flex items-center justify-center"><MapPin className="w-4 h-4 text-white" /></span>
        <div><div className="text-[11px] font-bold">Bengaluru</div><div className="text-[10px]" style={{ color: "var(--mute)" }}>Head Office · 42 people</div></div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section id="top" className="relative overflow-hidden pt-28 pb-16 lg:pt-36 lg:pb-24">
      <div className="absolute inset-0 -z-10" style={{ background: "var(--soft)" }} />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-semibold" style={{ borderColor: "var(--line)", background: "var(--soft)", color: "var(--ink2)" }}>
              <span className="w-2 h-2 rounded-full lp-grad-warm" />AI-powered workforce &amp; project platform
            </span>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[3.6rem] font-semibold leading-[1.05]" data-testid="lp-headline">
              <span className="lp-grad-text">AI insights.</span> Workforce clarity. Better decisions.
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-5 text-lg leading-relaxed max-w-xl" style={{ color: "var(--ink2)" }}>
              PRIZM brings your people, projects, engineering work and conversations into one workspace — with an executive dashboard and an AI that tells you what needs attention. Deployed as your own dedicated instance — built for companies with more than one team, and more than one branch.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/demo" className="lp-btn lp-btn-primary" data-testid="lp-cta-demo">Try the live demo <ArrowRight className="w-4 h-4" /></Link>
              <a href="#pricing" className="lp-btn lp-btn-ghost">See plans &amp; pricing</a>
            </div>
          </Reveal>
          <Reveal delay={320}>
            <div className="mt-9 flex flex-wrap gap-x-6 gap-y-2 text-sm" style={{ color: "var(--ink2)" }}>
              {["A dedicated deployment per company", "No shared database", "Role & branch based access"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><Check className="w-4 h-4" style={{ color: "#1fbf8a" }} />{t}</span>
              ))}
            </div>
          </Reveal>
        </div>
        <Reveal delay={200}><DashboardMock /></Reveal>
      </div>
    </section>
  );
}

function StatsStrip() {
  return (
    <section className="border-y" style={{ borderColor: "var(--line)", background: "var(--soft)" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
        {STATS.map((s) => (
          <div key={s.label}>
            <div className="lp-display text-4xl font-semibold lp-grad-text">{s.value}</div>
            <div className="text-sm mt-1" style={{ color: "var(--ink2)" }}>{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionHead({ eyebrow, title, sub, center = true }) {
  return (
    <Reveal className={center ? "text-center max-w-3xl mx-auto" : "max-w-2xl"}>
      <div className="text-xs font-bold uppercase tracking-[0.18em] lp-grad-text inline-block">{eyebrow}</div>
      <h2 className="mt-3 text-3xl sm:text-4xl font-semibold leading-tight">{title}</h2>
      {sub && <p className="mt-4 text-lg leading-relaxed" style={{ color: "var(--ink2)" }}>{sub}</p>}
    </Reveal>
  );
}

/* ───────────── Features ───────────── */
function Features() {
  return (
    <section id="features" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHead eyebrow="Everything in one place" title="One workspace for how your company actually runs" sub="Replace the patchwork of trackers, sheets and chat groups with a single system your whole organisation shares." />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => {
            const Icon = ICONS[f.icon] || Zap;
            return (
              <Reveal key={f.title} delay={(i % 4) * 70}>
                <div className="lp-card p-6 h-full" data-testid={`lp-feature-${i}`}>
                  <span className="w-11 h-11 rounded-xl flex items-center justify-center text-white" style={{ background: ICON_BG[i % ICON_BG.length] }}><Icon className="w-5 h-5" /></span>
                  <h3 className="mt-4 text-lg font-semibold">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--ink2)" }}>{f.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ───────────── Modules (tabs) ───────────── */
function Modules() {
  const [tab, setTab] = useState(MODULE_TABS[0].key);
  const cur = MODULE_TABS.find((t) => t.key === tab);
  return (
    <section id="modules" className="py-20 lg:py-28" style={{ background: "var(--soft)" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHead eyebrow="Modules" title="Every part of the business, connected" sub="Work, people, engineering, AI and conversation share the same data — so what you see in one place is true everywhere." />
        <Reveal>
          <div className="mt-10 flex flex-wrap justify-center gap-2" role="tablist" data-testid="lp-module-tabs">
            {MODULE_TABS.map((t) => (
              <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)} data-testid={`lp-tab-${t.key}`}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-all ${tab === t.key ? "text-white border-transparent lp-grad" : "bg-white hover:border-[#c9cff0]"}`}
                style={tab === t.key ? undefined : { borderColor: "var(--line)", color: "var(--ink2)" }}>{t.label}</button>
            ))}
          </div>
        </Reveal>
        <div className="mt-10 lp-card p-8 lg:p-10 grid lg:grid-cols-5 gap-8 items-start" key={tab}>
          <div className="lg:col-span-2">
            <h3 className="text-2xl font-semibold lp-grad-text inline-block">{cur.label}</h3>
            <p className="mt-2 text-lg" style={{ color: "var(--ink2)" }}>{cur.blurb}</p>
          </div>
          <ul className="lg:col-span-3 grid sm:grid-cols-2 gap-x-8 gap-y-3.5" data-testid="lp-module-items">
            {cur.items.map((it) => (
              <li key={it} className="flex gap-3 text-[15px] leading-snug" style={{ color: "var(--ink)" }}>
                <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full lp-grad flex items-center justify-center"><Check className="w-3 h-3 text-white" strokeWidth={3} /></span>{it}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ───────────── AI spotlight ───────────── */
function AiSpotlight() {
  return (
    <section className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <div className="text-xs font-bold uppercase tracking-[0.18em] lp-warm-text inline-block">PRIZM AI</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold leading-tight">Ask a question. Get the answer from your own data.</h2>
          <p className="mt-4 text-lg leading-relaxed" style={{ color: "var(--ink2)" }}>
            Press <kbd className="px-2 py-0.5 rounded-md border text-sm" style={{ borderColor: "var(--line)", background: "var(--soft)" }}>⌘K</kbd> anywhere to search documents, CRs, tasks and people — or open the AI Manager to ask about workload, delivery and performance. Suggested actions surface on the dashboard before small issues become big ones.
          </p>
          <ul className="mt-6 space-y-3">
            {["Answers scoped to what each person is allowed to see", "Suggested actions: coaching moments, risks, workload imbalances", "Tappy, the in-app bot, creates and updates tasks from chat"].map((t) => (
              <li key={t} className="flex gap-3 text-[15px]"><Zap className="w-5 h-5 shrink-0 text-[#F26B3A]" />{t}</li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={120}>
          <div className="rounded-3xl p-1 lp-grad shadow-[0_24px_60px_-30px_rgba(17,27,58,.35)]">
            <div className="rounded-[22px] bg-white p-5 space-y-4">
              <div className="flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm" style={{ borderColor: "var(--line)", color: "var(--mute)" }}>
                <Sparkles className="w-4 h-4 text-[#7C3AED]" />Which projects are at risk of missing their deadline?
              </div>
              <div className="rounded-xl p-4 text-sm leading-relaxed" style={{ background: "var(--soft)", color: "var(--ink)" }}>
                <b>2 projects need attention.</b> “Payments revamp” has 9 overdue tasks and one blocked epic; “Client portal” is 11 points behind plan. Moving two engineers from the lighter sprint would recover most of the gap.
                <div className="mt-3 flex flex-wrap gap-2">
                  {["Payments revamp", "Client portal", "View workload"].map((c) => <span key={c} className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white border" style={{ borderColor: "var(--line)", color: "var(--indigo)" }}>{c}</span>)}
                </div>
              </div>
              <div className="text-[10px] text-center" style={{ color: "var(--mute)" }}>Illustrative example</div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ───────────── Multi-branch ───────────── */
function Branches() {
  const TONE = { indigo: "#2B3BD9", violet: "#7C3AED", blue: "#3B82E6" };
  return (
    <section id="branches" className="py-20 lg:py-28 relative overflow-hidden" style={{ background: "var(--ink)" }}>
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal className="text-center max-w-3xl mx-auto">
          <div className="text-xs font-bold uppercase tracking-[0.18em] lp-warm-text inline-block">Multi-branch</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold leading-tight text-white">One company. Many branches. Clear ownership.</h2>
          <p className="mt-4 text-lg leading-relaxed text-white/70">Run offices in Bengaluru, London, Dubai or Singapore — in your own country or abroad — from one company account. Each branch has its own admin, everyone collaborates across cities and borders, and nobody edits what they don't own.</p>
        </Reveal>

        <Reveal delay={80}>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-3 text-sm" aria-hidden="true">
            <span className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-semibold text-white lp-grad"><Crown className="w-4 h-4" />Head Office</span>
            {["Bengaluru", "London", "Dubai", "Singapore", "New York"].map((c) => (
              <span key={c} className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-medium text-white/90 border border-white/20 bg-white/5"><MapPin className="w-4 h-4 text-[#6EE7A8]" />{c}</span>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {BRANCH_ROLES.map((r, i) => (
            <Reveal key={r.role} delay={i * 90}>
              <div className="rounded-2xl p-6 h-full border border-white/10 bg-white/[0.04] backdrop-blur" data-testid={`lp-role-${i}`}>
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ background: TONE[r.tone] }}>{i === 0 ? <Building2 className="w-5 h-5" /> : i === 1 ? <MapPin className="w-5 h-5" /> : <Users className="w-5 h-5" />}</span>
                  <h3 className="text-xl font-semibold text-white">{r.role}</h3>
                </div>
                <ul className="mt-5 space-y-3">
                  {r.can.map((c) => <li key={c} className="flex gap-2.5 text-[15px] text-white/80"><Check className="w-4 h-4 mt-1 shrink-0 text-[#6EE7A8]" />{c}</li>)}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={120}>
          <p className="mt-8 text-center text-sm text-white/60">A top-bar switcher scopes the dashboard, projects, people and reports to a single branch — or all of them — in one click.</p>
        </Reveal>
      </div>
    </section>
  );
}

/* ───────────── Deployment (isolation) + how it works ───────────── */
function Deployment() {
  const ICONS_D = [Server, Database, HardDrive, Lock];
  const companies = [["Company A", "#2B3BD9"], ["Company B", "#7C3AED"], ["Company C", "#F26B3A"]];
  return (
    <section id="deployment" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHead eyebrow="Dedicated deployment" title="Your own PRIZM. Nothing shared." sub="We deploy a separate PRIZM for every company — its own application, its own database, its own storage. No shared database, ever." />
        <div className="mt-14 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div className="grid grid-cols-3 gap-4" aria-hidden="true" data-testid="lp-isolation">
              {companies.map(([name, color], i) => (
                <div key={name} className="rounded-2xl border p-3.5 bg-white" style={{ borderColor: "var(--line)", boxShadow: `0 20px 40px -28px ${color}` }}>
                  <div className="text-[11px] font-bold mb-3 flex items-center gap-1.5" style={{ color }}><span className="w-2 h-2 rounded-full" style={{ background: color }} />{name}</div>
                  {[["App", Server], ["Database", Database], ["Storage", HardDrive]].map(([l, Ic]) => (
                    <div key={l} className="flex items-center gap-2 rounded-lg px-2.5 py-2 mb-2 last:mb-0 text-[11px] font-semibold" style={{ background: `${color}12`, color }}>
                      <Ic className="w-3.5 h-3.5" />{l}
                    </div>
                  ))}
                  <div className="mt-3 text-[10px] text-center font-medium" style={{ color: "var(--mute)" }}>Deployment {i + 1}</div>
                </div>
              ))}
              <div className="col-span-3 flex items-center justify-center gap-2 rounded-xl border border-dashed py-2.5 text-xs font-semibold" style={{ borderColor: "#c9cff0", color: "var(--ink2)" }}>
                <Lock className="w-3.5 h-3.5 text-[#E0409A]" />No shared database · no shared tables · no shared storage
              </div>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-4">
            {DEPLOY_POINTS.map((d, i) => {
              const Ic = ICONS_D[i % ICONS_D.length];
              return (
                <Reveal key={d.title} delay={i * 70}>
                  <div className="lp-card p-5 h-full">
                    <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ background: ICON_BG[i % ICON_BG.length] }}><Ic className="w-5 h-5" /></span>
                    <h3 className="mt-3 font-semibold">{d.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed" style={{ color: "var(--ink2)" }}>{d.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="py-16 lg:py-20" style={{ background: "var(--soft)" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHead eyebrow="How it works" title="From demo to your own PRIZM in three steps" />
        <div className="mt-12 grid md:grid-cols-3 gap-6" data-testid="lp-steps">
          {HOW_STEPS.map((st, i) => (
            <Reveal key={st.n} delay={i * 90}>
              <div className="lp-card p-6 h-full relative">
                <span className="lp-display w-11 h-11 rounded-full lp-grad text-white font-semibold text-lg flex items-center justify-center">{st.n}</span>
                <h3 className="mt-4 text-lg font-semibold">{st.title}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--ink2)" }}>{st.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal><div className="mt-10 text-center"><Link to="/demo" className="lp-btn lp-btn-primary" data-testid="lp-steps-cta">Try the live demo <ArrowRight className="w-4 h-4" /></Link></div></Reveal>
      </div>
    </section>
  );
}

/* ───────────── Pricing ───────────── */
function Pricing() {
  const [annual, setAnnual] = useState(false);
  return (
    <section id="pricing" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHead eyebrow="Plans & pricing" title="A one-time setup, then a simple rental" sub="Each company gets its own dedicated deployment. Pay a one-time setup fee, then rent it monthly or annually." />

        <Reveal>
          <div className="mt-8 flex items-center justify-center gap-3 text-sm font-semibold" data-testid="lp-billing-toggle">
            <span style={{ color: annual ? "var(--mute)" : "var(--ink)" }}>Monthly rental</span>
            <button role="switch" aria-checked={annual} onClick={() => setAnnual((v) => !v)} data-testid="lp-annual-switch"
              className="relative w-14 h-8 rounded-full transition-colors" style={{ background: annual ? "var(--violet)" : "#CBD1E8" }}>
              <span className="absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform" style={{ transform: annual ? "translateX(24px)" : "none" }} />
            </button>
            <span style={{ color: annual ? "var(--ink)" : "var(--mute)" }}>Annual rental</span>
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-full text-white lp-grad-warm">{ANNUAL_DISCOUNT_NOTE}</span>
          </div>
          <p className="mt-3 text-center text-xs" style={{ color: "var(--mute)" }}>All prices in US dollars (USD). Subscriptions and invoices are managed directly by our team.</p>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((p, i) => {
            const muted = p.highlight ? "rgba(255,255,255,.75)" : "var(--mute)";
            return (
              <Reveal key={p.key} delay={i * 90}>
                <div className={`relative h-full rounded-3xl p-7 flex flex-col ${p.highlight ? "text-white shadow-[0_24px_50px_-24px_rgba(17,27,58,.45)] lg:-translate-y-3" : "lp-card"}`}
                  style={p.highlight ? { background: "#2B3BD9" } : undefined} data-testid={`lp-plan-${p.key}`}>
                  {p.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-bold px-3 py-1 rounded-full text-white lp-grad-warm shadow">Most popular</span>}
                  <h3 className="text-xl font-semibold">{p.name}</h3>
                  <p className="mt-1 text-sm" style={{ color: p.highlight ? "rgba(255,255,255,.75)" : "var(--ink2)" }}>{p.tagline}</p>

                  <div className="mt-6 space-y-4">
                    <div data-testid={`lp-setup-${p.key}`}>
                      <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: muted }}>One-time setup</div>
                      <div className="lp-display text-3xl font-semibold mt-0.5">{p.setup != null ? fmtMoney(p.setup) : "Custom"}</div>
                    </div>
                    <div className="h-px" style={{ background: p.highlight ? "rgba(255,255,255,.25)" : "var(--line)" }} />
                    <div data-testid={`lp-rental-${p.key}`}>
                      <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: muted }}>{annual ? "Annual rental" : "Monthly rental"}</div>
                      {p.price ? (
                        <>
                          <div className="flex items-end gap-1.5 mt-0.5">
                            <span className="lp-display text-5xl font-semibold" data-testid={`lp-price-${p.key}`}>{fmtMoney(annual ? Math.round(p.price.annual / 12) : p.price.monthly)}</span>
                            <span className="pb-1.5 text-sm" style={{ color: muted }}>/ month</span>
                          </div>
                          <div className="text-xs mt-1" style={{ color: muted }}>{annual ? `${fmtMoney(p.price.annual)} billed yearly` : "billed monthly"}</div>
                        </>
                      ) : (
                        <div className="lp-display text-5xl font-semibold mt-0.5">Custom</div>
                      )}
                    </div>
                  </div>

                  <ul className="mt-7 space-y-3 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex gap-2.5 text-[15px]">
                        <Check className="w-4 h-4 mt-1 shrink-0" style={{ color: p.highlight ? "#6EE7A8" : "#1fbf8a" }} strokeWidth={3} />{f}
                      </li>
                    ))}
                  </ul>
                  <Link to={`/demo?plan=${p.key}`} className={`lp-btn justify-center mt-8 ${p.highlight ? "bg-white text-[color:var(--indigo)] hover:bg-white/90" : p.price ? "lp-btn-primary" : "lp-btn-ghost"}`} data-testid={`lp-plan-cta-${p.key}`}>{p.cta} <ArrowRight className="w-4 h-4" /></Link>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ───────────── Trust ───────────── */
function Trust() {
  const items = [
    { icon: ShieldCheck, title: "Role-based access", body: "Company Admins, Branch Admins, managers and employees each see and change only what they should — enforced on the server, not just in the UI." },
    { icon: Cloud, title: "Your data, your deployment", body: "Each company runs on its own deployment with its own database and file storage — no shared database — and files are served only to authorised users." },
    { icon: Building2, title: "Your brand", body: "Replace the PRIZM logo with your company logo — in the sidebar and on the sign-in page — from Settings." },
  ];
  return (
    <section className="py-16 lg:py-20" style={{ background: "var(--soft)" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-3 gap-6">
        {items.map((it, i) => (
          <Reveal key={it.title} delay={i * 80}>
            <div className="flex gap-4">
              <span className="w-12 h-12 shrink-0 rounded-2xl lp-grad flex items-center justify-center text-white"><it.icon className="w-6 h-6" /></span>
              <div><h3 className="text-lg font-semibold">{it.title}</h3><p className="mt-1.5 text-sm leading-relaxed" style={{ color: "var(--ink2)" }}>{it.body}</p></div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ───────────── FAQ ───────────── */
function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-20 lg:py-28">
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <SectionHead eyebrow="FAQ" title="Questions, answered" />
        <div className="mt-10 space-y-3" data-testid="lp-faq">
          {FAQ.map((f, i) => (
            <Reveal key={f.q} delay={i * 30}>
              <div className="lp-card !rounded-2xl overflow-hidden">
                <button className="w-full flex items-center justify-between gap-4 text-left px-6 py-4 font-semibold" onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i} data-testid={`lp-faq-q-${i}`}>
                  {f.q}<ChevronDown className="w-5 h-5 shrink-0 transition-transform" style={{ transform: open === i ? "rotate(180deg)" : "none", color: "var(--violet)" }} />
                </button>
                {open === i && <div className="px-6 pb-5 text-[15px] leading-relaxed" style={{ color: "var(--ink2)" }} data-testid={`lp-faq-a-${i}`}>{f.a}</div>}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────── CTA + footer ───────────── */
function FinalCta() {
  return (
    <section className="px-5 lg:px-8 pb-20">
      <Reveal>
        <div style={{ background: "var(--ink)" }} className="max-w-6xl mx-auto rounded-[2rem] px-6 py-14 lg:py-16 text-center text-white relative overflow-hidden shadow-[0_24px_60px_-30px_rgba(17,27,58,.5)]">
          <h2 className="relative text-3xl sm:text-4xl font-semibold leading-tight max-w-2xl mx-auto">Give every branch the same clear picture.</h2>
          <p className="relative mt-4 text-white/80 max-w-xl mx-auto">Explore the live demo, then we'll deploy a dedicated PRIZM for your company and help you migrate.</p>
          <div className="relative mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/demo" className="lp-btn bg-white text-[color:var(--indigo)] hover:bg-white/90" data-testid="lp-final-demo">Try the live demo <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/login" className="lp-btn border border-white/40 text-white hover:bg-white/10">Sign in</Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t" style={{ borderColor: "var(--line)" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
        <div><img src="/prizm-logo-light.png" alt="PRIZM" className="h-9 w-auto" /></div>
        <nav className="flex flex-wrap justify-center gap-x-7 gap-y-2 text-sm" style={{ color: "var(--ink2)" }}>
          {NAV.map((n) => <a key={n.href} href={n.href} className="hover:text-[color:var(--indigo)]">{n.label}</a>)}
          <Link to="/login" className="hover:text-[color:var(--indigo)]">Sign in</Link>
          <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-[color:var(--indigo)]">Contact</a>
        </nav>
        <div className="text-xs" style={{ color: "var(--mute)" }}>© {new Date().getFullYear()} PRIZM. All rights reserved.</div>
      </div>
    </footer>
  );
}

export default function Landing() {
  useEffect(() => {
    const prev = document.title;
    document.title = "PRIZM · AI Insights. Workforce Clarity. Better Decisions.";
    return () => { document.title = prev; };
  }, []);
  return (
    <div className="lp min-h-screen" style={{ scrollBehavior: "smooth" }} data-testid="landing-page">
      <style>{CSS}</style>
      <Navbar />
      <main>
        <Hero />
        <StatsStrip />
        <Features />
        <Modules />
        <AiSpotlight />
        <Branches />
        <Deployment />
        <HowItWorks />
        <Pricing />
        <Trust />
        <Faq />
        <FinalCta />
      </main>
      <Footer />
    </div>
  );
}
